/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import React, { ReactElement, ReactNode } from "react"
import { Link } from "react-router-dom"
import MedaicaLogo from "@medaica/common/components/medaica-logo"
import LinkList from "@medaica/common/components/link-list"
import { footerNavigation } from "../../../const"
import bookingCopy from "../bookingCopy"
import { bookingColors } from "../booking-theme"

/**
 * Shared chrome for every booking screen — matches patient.medaica.com: a white top bar with the
 * Medaica logo (logo blue) + a thin divider + the "scheduling" label at left, an optional
 * per-screen right-hand slot, and the portal's footer nav. The teal PDF chrome is retired.
 * `appName` lets another small flow (the patient Medaica Report download page) reuse this
 * chrome under its own header label.
 */
const BookingLayout = ({
  appName = bookingCopy.appName,
  headerRight,
  children,
}: {
  appName?: string
  headerRight?: ReactNode
  children: ReactNode
}): ReactElement => (
  // `booking-shell` scopes the wizard's mobile input font-size rule (see patient index.css).
  <div className="booking-shell min-h-screen flex flex-col bg-white">
    <header className="flex-shrink-0 flex items-center justify-between h-16 px-4 border-b">
      <div className="flex flex-shrink-0 items-center">
        {/* Links to "/" like the portal's GlobalLayout — same behavior as localhost:3000. */}
        <Link to="/" aria-label="Medaica home">
          <MedaicaLogo className="text-primary h-6" />
        </Link>
        <span className="mx-3 h-5 w-px bg-gray-300" aria-hidden="true" />
        <span className="font-medium text-gray-600">{appName}</span>
      </div>
      {/* min-w-0 + truncate: a long identity ("John · jo…son@gmail.com") must shorten on a
          narrow phone instead of colliding with the left cluster inside the fixed h-16 bar. */}
      {headerRight ? <div className="ml-3 min-w-0 truncate text-right text-sm text-gray-500">{headerRight}</div> : null}
    </header>
    <main className="flex-1" style={{ backgroundColor: bookingColors.surface }}>
      {children}
    </main>
    <footer className="flex-shrink-0 border-t border-gray-200 py-4 px-4">
      <LinkList
        links={footerNavigation}
        ulClassname="flex flex-wrap justify-center gap-x-5 gap-y-2 text-sm"
        aClassname="link"
      />
    </footer>
  </div>
)

export default BookingLayout
